import React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useFetchGradesPerComponentAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import { Alert, Card, Spin } from 'antd'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { useThemeColors } from '../../../../../../../hooks/useThemeColors'

const GradesHistogram = ({ token, selectedComponentIndex = 0, showOnlyCurrentStudents = false }) => {
    const colors = useThemeColors()
    const { data: analytics, isFetching, error } = useFetchGradesPerComponentAnalyticsQuery({ token })

    const data = analytics && [
        ...analytics.current[selectedComponentIndex].distribution.map(distributionItem => ({
            range: distributionItem.range,
            percentOfStudents: distributionItem.percentOfStudents,
            numberOfStudents: distributionItem.numberOfStudents,
            name: showOnlyCurrentStudents ? 'Students' : 'My students',
            fill: colors.primary,
        })),
        ...(showOnlyCurrentStudents
            ? []
            : analytics.all[selectedComponentIndex].distribution.map(distributionItem => ({
                  range: distributionItem.range,
                  percentOfStudents: distributionItem.percentOfStudents,
                  numberOfStudents: distributionItem.numberOfStudents,
                  name: 'All students',
                  fill: colors.success,
              }))),
    ]

    // Custom tooltip component
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const range = payload[0]?.payload?.range;
            const entries = payload.filter(entry => entry.value !== undefined && entry.value !== null);
            
            if (!entries.length || !range) return null;
            
            return (
                <Card size='small'>
                    <Title level={5} style={{ margin: 0, fontSize: '0.9rem' }}>
                        {`Grade in ${range} range`}
                    </Title>
                    {entries.map((entry, index) => {
                        // Find original data with number of students
                        const originalData = data.find(item => 
                            item.range === range && 
                            item.name === entry.dataKey
                        );
                        
                        return (
                            <Text key={index} style={{ display: 'block' }}>
                                {`${entry.name}: ${entry.value}% - ${originalData?.numberOfStudents || 0} students`}
                            </Text>
                        );
                    })}
                </Card>
            );
        }
        return null;
    };

    // Generate a detailed description of the data for accessibility
    const generateDescription = () => {
        if (!data) return 'Loading data...'
        let description = ''
        if (analytics?.numberOfCurrentStudents)
            description += `Total number of ${showOnlyCurrentStudents ? '' : 'my'} students: ${
                analytics.numberOfCurrentStudents
            } students. `
        if (analytics?.numberOfAllStudents)
            description += `Total number of all students: ${analytics.numberOfAllStudents} students. `
        description += "Detailed breakdown of students' grades. "
        data.forEach(item => {
            if (item.numberOfStudents === 0) return
            description += `In the ${item.range} range there are ${item.numberOfStudents} of ${item.name} making up ${item.percentOfStudents}% of the total. `
        })
        return description
    }

    if (error) return <Alert message={error} type='error' showIcon />

    // Group data by range and create series for each student group
    const chartData = data && analytics ? 
        Array.from(new Set(data.map(item => item.range))).map(range => {
            const result = { range };
            const myStudentsItem = data.find(item => item.range === range && (item.name === 'My students' || item.name === 'Students'));
            const allStudentsItem = data.find(item => item.range === range && item.name === 'All students');
            
            if (myStudentsItem) {
                result[myStudentsItem.name] = myStudentsItem.percentOfStudents;
                result[`${myStudentsItem.name}-count`] = myStudentsItem.numberOfStudents;
            }
            
            if (allStudentsItem) {
                result['All students'] = allStudentsItem.percentOfStudents;
                result[`All students-count`] = allStudentsItem.numberOfStudents;
            }
            
            return result;
        }) : [];

    return (
        <Spin spinning={isFetching} tip='Loading grades analytics...'>
            <div
                role='img'
                aria-label='Histogram of grades showing the distribution of students across different grade ranges'
                aria-describedby={`gradesChartDescription-${token}`}
            >
                <ResponsiveContainer width="100%" height={480}>
                    <BarChart 
                        data={chartData} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="range"
                            label={{ 
                                value: 'Grade', 
                                position: 'bottom', 
                                offset: 0,
                                dy: 20
                            }}
                        />
                        <YAxis 
                            tickFormatter={value => `${value}%`}
                            label={{ 
                                value: 'Percent of Students', 
                                angle: -90, 
                                position: 'insideLeft',
                                dx: -20
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        {!showOnlyCurrentStudents && (
                            <Bar 
                                dataKey="All students" 
                                fill={colors.success} 
                                radius={[4, 4, 0, 0]}
                                name="All students"
                                label={{ 
                                    position: 'top', 
                                    formatter: value => `${value}%`,
                                    fill: '#000'
                                }}
                            />
                        )}
                        <Bar 
                            dataKey={showOnlyCurrentStudents ? "Students" : "My students"} 
                            fill={colors.primary} 
                            radius={[4, 4, 0, 0]} 
                            name={showOnlyCurrentStudents ? "Students" : "My students"}
                            label={{ 
                                position: 'top', 
                                formatter: value => `${value}%`,
                                fill: '#000'
                            }}
                        />
                    </BarChart>
                </ResponsiveContainer>
                <div id={`gradesChartDescription-${token}`} className='visually-hidden focusable' tabIndex='0'>
                    {generateDescription()}
                </div>
            </div>
        </Spin>
    )
}

export default GradesHistogram
